import React, { useState, useEffect } from "react";
import { AppTextField } from "components/common/inputs";
import { AppDialog } from "components/common/dialogs";
import {
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";
import {
  activateUser,
  updateUser,
  uploadAvatar,
  removeAvatar,
} from "../../services/user.service";
import { getPersonnelById } from "../../services/personnel/personnel.service";
import PersonnelPickerDialog from "./PersonnelPickerDialog";
import { getFullAvatarUrl } from "utils/imageUtils";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import "./UserModals.css";
import { AppButton } from "components/common/buttons";

const EditUserModal = ({ open, onClose, user, onUserUpdated }) => {
  // Initialize state with empty values, will be populated in useEffect
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    personnelId: null,
    isActive: false,
    removeAvatar: false, // Flag to indicate if avatar should be removed
  });
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [personnelPickerOpen, setPersonnelPickerOpen] = useState(false);
  const [linkedPersonnelState, setLinkedPersonnelState] = useState({ personnelId: null, loading: false, error: "" });
  const [linkedPersonnelReload, setLinkedPersonnelReload] = useState(0);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false); // State for avatar loading operations

  useEffect(() => {
    setPersonnelPickerOpen(false);
    setSelectedPersonnel(user?.personnelId ? {
      personnelId: user.personnelId,
      fullName: user.personnelName || user.personnelFullName || "",
    } : null);
    if (user) {
      // Reset all states
      setSuccess(false); // Ensure success message is cleared
      setError(null); // Clear any errors
      setFormErrors({});

      // Reset form data
      setFormData({
        fullName: user.fullName || "",
        email: user.email || "",
        personnelId: user.personnelId || null,
        isActive: Boolean(user.isActive),
        removeAvatar: false,
      });

      // Format avatar URL properly
      const fullAvatarUrl = user.avatarFilePath
        ? getFullAvatarUrl(user.avatarFilePath)
        : "";

      // Update state
      setAvatarPreview(fullAvatarUrl);
      setAvatarFile(null);
    }
  }, [user, open]);

  useEffect(() => {
    const personnelId = user?.personnelId;
    if (!open || !personnelId || user.personnelName || user.personnelFullName) {
      setLinkedPersonnelState({ personnelId: null, loading: false, error: "" });
      return;
    }
    let current = true;
    setLinkedPersonnelState({ personnelId, loading: true, error: "" });
    getPersonnelById(personnelId).then((response) => {
      const personnel = response.data?.data ?? response.data?.Data ?? response.data;
      const fullName = personnel?.fullName ?? personnel?.FullName ?? personnel?.personnelName ?? personnel?.PersonnelName;
      if (!fullName) throw new Error("Missing linked personnel name");
      if (!current) return;
      // A pending request must not replace a personnel selection made in the picker.
      setSelectedPersonnel((previous) => previous?.personnelId === personnelId ? { ...previous, fullName } : previous);
      setLinkedPersonnelState({ personnelId, loading: false, error: "" });
    }).catch(() => {
      if (current) setLinkedPersonnelState({ personnelId, loading: false,
        error: "Không thể tải tên nhân sự liên kết. Liên kết hiện tại vẫn được giữ.",
      });
    });
    return () => { current = false; };
  }, [open, user, linkedPersonnelReload]);

  const handlePersonnelSelect = (personnel) => {
    setSelectedPersonnel(personnel);
    setPersonnelPickerOpen(false);
    setSuccess(false);
    setError(null);
    setFormData((previous) => ({
      ...previous,
      personnelId: personnel?.personnelId || null,
      fullName: previous.fullName || personnel?.fullName || "",
      email: previous.email || personnel?.email || "",
      isActive: personnel ? previous.isActive : false,
    }));
  };

  const handleChange = (e) => {
    // Reset success state when user makes changes
    setSuccess(false);

    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  const handleSwitchChange = (e) => {
    setSuccess(false);

    if (e.target.checked && !formData.personnelId) {
      setError("Chưa gán nhân sự nên không kích hoạt được tài khoản.");
      setFormData((prev) => ({
        ...prev,
        isActive: false,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset success state since we're making new changes
    setSuccess(false);

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setError("Chỉ chấp nhận file ảnh định dạng JPG, PNG hoặc GIF.");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Kích thước ảnh không được vượt quá 5MB.");
      return;
    }

    // Set the avatar file
    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Clear the removeAvatar flag since we're adding a new avatar
    setFormData((prev) => ({
      ...prev,
      removeAvatar: false,
    }));

    // Clear any previous errors
    setError(null);
  };

  const handleRemoveAvatar = () => {
    // Reset success state since we're making new changes
    setSuccess(false);

    // Mark avatar for deletion
    setAvatarFile(null);
    setAvatarPreview("");

    // Set a flag to indicate that the avatar should be removed
    setFormData((prev) => ({
      ...prev,
      removeAvatar: true,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email không hợp lệ";
    }

    return errors;
  };

  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Update user info
      await updateUser(user.userId, {
        fullName: formData.fullName || null,
        email: formData.email || null,
        personnelId: formData.personnelId || null,
      }); // Handle avatar changes

      // Clearing PersonnelId also deactivates the account in the update endpoint.
      if (formData.personnelId && formData.isActive !== Boolean(user.isActive)) {
        await activateUser(user.userId, formData.isActive);
      }

      // Handle avatar changes
      if (formData.removeAvatar) {
        try {
          setAvatarLoading(true); // Start loading for avatar removal

          // Log user info để debug

          // Kiểm tra user ID
          if (!user.userId) {
            throw new Error("User ID is undefined or invalid");
          } // Remove avatar if flagged for removal
          await removeAvatar(user.userId);

          // Xóa giá trị avatar trong state để đồng bộ UI
          setAvatarPreview("");
        } catch (avatarErr) {
          console.error("Error removing avatar:", avatarErr); // Hiển thị thông báo lỗi chi tiết hơn và phản hồi từ API
          let errorMessage;

          if (avatarErr.response) {
            // Nếu có phản hồi từ API
            if (avatarErr.response.status === 404) {
              errorMessage =
                "Không thể tìm thấy người dùng để xóa ảnh đại diện.";
            } else if (
              avatarErr.response.data &&
              avatarErr.response.data.message
            ) {
              errorMessage = avatarErr.response.data.message;
            } else {
              errorMessage = `Lỗi từ server (${avatarErr.response.status}): Không thể xóa ảnh đại diện, thông tin khác đã được cập nhật.`;
            }
          } else if (avatarErr.request) {
            // Yêu cầu được gửi nhưng không nhận được phản hồi
            errorMessage =
              "Không thể kết nối đến máy chủ để xóa ảnh đại diện. Vui lòng thử lại sau.";
          } else {
            // Có lỗi khi thiết lập yêu cầu
            errorMessage = `Lỗi: ${avatarErr.message ||
              "Không thể xóa ảnh đại diện, thông tin khác đã được cập nhật."
              }`;
          }

          setError(errorMessage);

          // Log chi tiết về lỗi để giúp debug

        } finally {
          setAvatarLoading(false); // End loading regardless of result
        }
      } else if (avatarFile) {
        // Upload new avatar if there's a file selected
        const avatarFormData = new FormData();
        avatarFormData.append("file", avatarFile);
        try {
          setAvatarLoading(true); // Start loading for avatar upload
          await uploadAvatar(user.userId, avatarFormData);

        } catch (avatarErr) {
          console.error("Error uploading avatar:", avatarErr);
          // Show warning but continue with other operations
          setError(
            (prevError) =>
              `${prevError ? prevError + " " : ""
              }Không thể tải lên ảnh đại diện, thông tin khác đã được cập nhật.`
          );
        } finally {
          setAvatarLoading(false); // Stop loading
        }
      } // Hiển thị thông báo thành công với các điều kiện phụ nếu có lỗi avatar
      if (error) {
        // Nếu chỉ có lỗi khi xóa avatar nhưng thông tin user đã được cập nhật thành công
        setSuccess(true);

        // Tự động đóng dialog sau một khoảng thời gian nhưng để người dùng vẫn thấy cảnh báo về avatar
        setTimeout(() => {
          // Reset success state before closing
          setSuccess(false);
          onUserUpdated();
          onClose();
        }, 3000); // Tăng thời gian hiển thị lên 3 giây để đảm bảo người dùng nhìn thấy thông báo lỗi
      } else {
        // Nếu mọi thứ đều thành công
        setSuccess(true);

        // Close modal and refresh data after showing success message briefly
        setTimeout(() => {
          // Reset success state before closing
          setSuccess(false);
          onUserUpdated();
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error updating user:", err);
      if (err.response && err.response.data) {
        setError(
          err.response.data.message ||
          "Có lỗi xảy ra khi cập nhật thông tin người dùng."
        );
      } else {
        setError(
          "Có lỗi xảy ra khi cập nhật thông tin người dùng. Vui lòng thử lại sau."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPersonnelPickerOpen(false);
    // Reset all states first
    setFormErrors({});
    setError(null);
    setSuccess(false);
    setAvatarFile(null);
    setAvatarPreview(
      user?.avatarFilePath ? getFullAvatarUrl(user.avatarFilePath) : ""
    ); // Reset avatar preview to original

    // Reset form data to initial values
    if (user) {
      setFormData({
        fullName: user.fullName || "",
        email: user.email || "",
        personnelId: user.personnelId || null,
        isActive: Boolean(user.isActive),
        removeAvatar: false,
      });
    }

    // Close the modal
    onClose();
  };

  if (!user) return null;

  const linkedNamePending = Boolean(formData.personnelId) && !selectedPersonnel?.fullName &&
    linkedPersonnelState.personnelId === formData.personnelId;

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={`Chỉnh sửa người dùng: ${user.userName}`}
      size="sm"
      loading={isSubmitting}
      actions={
        <>
          <AppButton
            onClick={handleClose}
            disabled={isSubmitting}
            variant="outlined"
            color="inherit"
          >
            Hủy
          </AppButton>
          <AppButton
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || success}
            disableElevation
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Lưu thay đổi"}
          </AppButton>
        </>
      }
    >
        {error && (
          <Alert severity="error" className="alert-margin">
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" className="alert-margin">
            Cập nhật thông tin thành công!
          </Alert>
        )}
        <div className="avatar-upload-section">
          <div className={formData.removeAvatar ? "avatar-to-be-removed" : ""}>
            {avatarLoading ? (
              <CircularProgress size={100} />
            ) : (
              <Avatar
                src={avatarPreview}
                alt={user.fullName || user.userName}
                sx={{ width: 100, height: 100 }}
              >
                {user.fullName
                  ? user.fullName.charAt(0).toUpperCase()
                  : user.userName.charAt(0).toUpperCase()}
              </Avatar>
            )}
            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === "development" && (
              <div style={{ display: "none" }}>Avatar URL: {avatarPreview}</div>
            )}
          </div>
          {formData.removeAvatar && (
            <div className="avatar-feedback">
              Ảnh đại diện sẽ bị xóa khi lưu thay đổi
            </div>
          )}
          {avatarFile && !formData.removeAvatar && (
            <div className="avatar-feedback">
              Ảnh đại diện mới sẽ được cập nhật khi lưu thay đổi
            </div>
          )}
          <div className="avatar-actions">
            <IconButton
              color="primary"
              aria-label="upload avatar"
              component="label"
              disabled={isSubmitting || avatarLoading || success}
              title="Thay đổi ảnh đại diện"
              disableElevation
              sx={{ boxShadow: "none" }}
            >
              <input
                hidden
                accept="image/jpeg,image/png,image/gif"
                type="file"
                onChange={handleAvatarChange}
              />
              <PhotoCamera />
            </IconButton>

            {(avatarPreview || avatarFile) && !formData.removeAvatar && (
              <IconButton
                color="secondary"
                aria-label="remove avatar"
                onClick={handleRemoveAvatar}
                disabled={isSubmitting || avatarLoading || success}
                title="Xóa ảnh đại diện"
                disableElevation
                sx={{ boxShadow: "none" }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </div>
        <div className="form-row">
          <AppTextField
            name="fullName"
            label="Tên người dùng"
            value={formData.fullName}
            onChange={(_, event) => handleChange(event)}
            fullWidth
            disabled={isSubmitting || success}
            margin="normal"
            helperText="Tự động lấy theo nhân sự liên kết, có thể chỉnh sửa"
          />
        </div>
        <div className="form-row">
          <AppTextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={(_, event) => handleChange(event)}
            fullWidth
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={isSubmitting || success}
            margin="normal"
          />
        </div>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
          <AppTextField
            label="Nhân sự liên kết"
            value={formData.personnelId ? selectedPersonnel?.fullName || `Nhân sự #${formData.personnelId}` : ""}
            placeholder="Chưa chọn nhân sự"
            InputProps={{ readOnly: true }}
            sx={{ flex: 1 }}
            disabled={isSubmitting || success}
            endIcon={linkedNamePending && linkedPersonnelState.loading ? <CircularProgress size={18} aria-label="Đang tải tên nhân sự" /> : undefined}
            error={linkedNamePending && !!linkedPersonnelState.error}
            helperText={(linkedNamePending && linkedPersonnelState.error) || (!formData.personnelId ? "Chưa chọn nhân sự thì tài khoản không thể kích hoạt." : "")}
          />
          <AppButton variant="outlined" onClick={() => setPersonnelPickerOpen(true)}
            disabled={isSubmitting || avatarLoading || success} sx={{ flexShrink: 0 }}>Chọn nhân sự</AppButton>
          {!!formData.personnelId && <AppButton size="small" color="inherit" onClick={() => handlePersonnelSelect(null)}
            disabled={isSubmitting || avatarLoading || success}>Bỏ liên kết</AppButton>}
        </Box>
        {linkedNamePending && linkedPersonnelState.error && <AppButton
          disabled={isSubmitting || success} onClick={() => setLinkedPersonnelReload((count) => count + 1)}>Thử lại</AppButton>}
        {open && personnelPickerOpen && <PersonnelPickerDialog
          selectedPersonnelId={formData.personnelId} onClose={() => setPersonnelPickerOpen(false)}
          onSelect={handlePersonnelSelect} />}
        {!formData.personnelId && (
          <Alert severity="warning" className="alert-margin">
            Chưa liên kết nhân sự nên tài khoản không thể kích hoạt.
          </Alert>
        )}
        <div className="form-row">
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.isActive)}
                onChange={handleSwitchChange}
                color="primary"
                disabled={isSubmitting || avatarLoading || success || !formData.personnelId}
              />
            }
            label="Kích hoạt tài khoản"
          />
        </div>
    </AppDialog>
  );
};

export default EditUserModal;
